import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { addDays, format } from 'date-fns';
import { Play } from 'lucide-react';
import {
  WEEKDAY_LABELS,
  type GenerateSlotsResult,
  type Weekday,
  type ScheduleTemplateSummary,
} from '@hch/shared';
import { Button } from '~/components/ui/button';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
} from '~/components/ui/primitives';
import { LoadingState, PageHeader } from '~/components/shared/states';
import { api } from '~/lib/api';
import { queryKeys, messageOf } from '~/lib/queries';
import { formatDate, formatDateTime, formatDuration, fullName } from '~/lib/utils';

export function AdminTemplatesPage() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: queryKeys.templates,
    queryFn: () => api.get<ScheduleTemplateSummary[]>('/schedule-templates'),
  });

  const [generating, setGenerating] = React.useState<ScheduleTemplateSummary | null>(null);
  const [preview, setPreview] = React.useState<GenerateSlotsResult | null>(null);
  const [range, setRange] = React.useState({
    from: format(new Date(), 'yyyy-MM-dd'),
    to: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
  });
  const [busy, setBusy] = React.useState(false);

  /**
   * Deux temps volontaires : on simule d'abord (`dryRun`), on n'ecrit qu'apres
   * confirmation. Generer un mois de creneaux est une action a fort impact.
   */
  async function simulate(template: ScheduleTemplateSummary) {
    setBusy(true);
    try {
      setPreview(
        await api.post<GenerateSlotsResult>(`/schedule-templates/${template.id}/generate`, {
          ...range,
          dryRun: true,
        }),
      );
    } catch (error) {
      toast.error(messageOf(error));
    } finally {
      setBusy(false);
    }
  }

  async function apply(template: ScheduleTemplateSummary) {
    setBusy(true);
    try {
      const result = await api.post<GenerateSlotsResult>(
        `/schedule-templates/${template.id}/generate`,
        { ...range, dryRun: false },
      );
      toast.success(`${result.created} creneaux generes.`);
      setGenerating(null);
      setPreview(null);
      await refetch();
    } catch (error) {
      toast.error(messageOf(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-page">
      <PageHeader
        title="Modeles de planification"
        description="Chaque modele decrit le rythme de travail d'un technicien et genere ses creneaux."
      />

      {isLoading ? <LoadingState /> : null}

      <div className="grid gap-3 lg:grid-cols-2">
        {data?.map((template) => (
          <Card key={template.id}>
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-sm">{template.name}</CardTitle>
                <Badge variant={template.isActive ? 'success' : 'outline'}>
                  {template.isActive ? 'Actif' : 'Inactif'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="app-summary-row">
                <span className="text-muted-foreground">Technicien</span>
                <span className="font-medium">{fullName(template.technician)}</span>
              </div>
              <div className="app-summary-row">
                <span className="text-muted-foreground">Zone</span>
                <span className="flex items-center gap-1.5 font-medium">
                  <span
                    className="size-2 rounded-full"
                    style={{ backgroundColor: template.zone.color }}
                    aria-hidden
                  />
                  {template.zone.name}
                </span>
              </div>
              <div className="app-summary-row">
                <span className="text-muted-foreground">Horaires</span>
                <span className="font-medium tabular-nums">
                  {template.startTime} – {template.endTime}
                  {template.breakStartTime
                    ? ` (pause ${template.breakStartTime}–${template.breakEndTime})`
                    : null}
                </span>
              </div>
              <div className="app-summary-row">
                <span className="text-muted-foreground">Granularite</span>
                <span className="font-medium">{formatDuration(template.slotDurationMin)}</span>
              </div>

              <div className="flex flex-wrap gap-1">
                {template.weekdays.map((day) => (
                  <Badge key={day} variant="secondary">
                    {WEEKDAY_LABELS[day as Weekday]}
                  </Badge>
                ))}
              </div>

              <p className="text-xs text-muted-foreground">
                Valide du {formatDate(template.validFrom)} au {formatDate(template.validTo)} ·{' '}
                {template.slotCount} creneaux generes
              </p>

              <Button
                size="sm"
                variant="outline"
                className="w-full"
                onClick={() => {
                  setGenerating(template);
                  setPreview(null);
                }}
              >
                <Play aria-hidden />
                Generer des creneaux
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!generating} onOpenChange={(open) => !open && setGenerating(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generer les creneaux — {generating?.name}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Du" htmlFor="from" required>
              <Input
                type="date"
                value={range.from}
                onChange={(e) => setRange({ ...range, from: e.target.value })}
              />
            </Field>
            <Field label="Au" htmlFor="to" required>
              <Input
                type="date"
                value={range.to}
                onChange={(e) => setRange({ ...range, to: e.target.value })}
              />
            </Field>
          </div>

          {preview ? (
            <div className="app-summary-box space-y-1.5 text-sm">
              <p className="font-medium">Simulation</p>
              <div className="app-summary-row">
                <span className="text-muted-foreground">A creer</span>
                <span className="font-semibold tabular-nums text-success">{preview.created}</span>
              </div>
              <div className="app-summary-row">
                <span className="text-muted-foreground">Deja existants (ignores)</span>
                <span className="tabular-nums">{preview.skippedExisting}</span>
              </div>
              <div className="app-summary-row">
                <span className="text-muted-foreground">En conflit (ignores)</span>
                <span className="tabular-nums">{preview.skippedConflict}</span>
              </div>
              {preview.firstSlotAt ? (
                <p className="text-xs text-muted-foreground">
                  Du {formatDateTime(preview.firstSlotAt)} au {formatDateTime(preview.lastSlotAt!)}
                </p>
              ) : null}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerating(null)}>
              Fermer
            </Button>
            {!preview ? (
              <Button loading={busy} onClick={() => generating && void simulate(generating)}>
                Simuler
              </Button>
            ) : (
              <Button
                loading={busy}
                disabled={preview.created === 0}
                onClick={() => generating && void apply(generating)}
              >
                Appliquer ({preview.created})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Utilisateurs
